import { useInfiniteQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { get_automation_runs_url } from '@/utils/urls.ts';
import { AUTOMATION_RUNS_KEY } from '@/utils/queryKeys.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import type { ExecutionStatus } from '@/types/statuses.ts';
import {
  getRunProvenanceQueryStates,
  isDefaultRunStatusSelection,
  RunMode,
  RunOrigin,
  RunProvenanceState,
  Runs,
} from '@/types/runs.ts';

type QueryProps = {
  statuses?: ExecutionStatus[];
  search?: string;
  providedAutomationId?: string;
  runModes?: RunMode[];
  runOrigins?: RunOrigin[];
  provenanceStates?: RunProvenanceState[];
  enabled?: boolean;
};

const normalizeFilterValues = <T extends string>(values: T[] | undefined): T[] =>
  [...new Set(values ?? [])].sort();

const useRetrieveAutomationRuns = ({
  statuses,
  search,
  providedAutomationId,
  runModes,
  runOrigins,
  provenanceStates,
  enabled = true,
}: QueryProps) => {
  const apiClient = useApiClient();
  const detectedAutomationId = useAutomationId();
  const automationId = providedAutomationId || detectedAutomationId;
  const normalizedRunModes = normalizeFilterValues(runModes ?? [RunMode.Operational]);
  const normalizedStatuses = normalizeFilterValues(statuses);
  const statusQueryValues = isDefaultRunStatusSelection(normalizedStatuses)
    ? []
    : normalizedStatuses;
  const normalizedRunOrigins = normalizeFilterValues(runOrigins);
  const normalizedProvenanceStates = normalizeFilterValues(provenanceStates);
  const provenanceQueryStates = getRunProvenanceQueryStates(normalizedProvenanceStates);

  return useInfiniteQuery<Runs, AxiosError>({
    queryKey: [
      ...AUTOMATION_RUNS_KEY(automationId),
      statusQueryValues.join(','),
      search,
      normalizedRunModes.join(','),
      normalizedRunOrigins.join(','),
      provenanceQueryStates.join(','),
    ],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      statusQueryValues.forEach((value) => params.append('status', value));
      if (search) params.append('search', search);
      normalizedRunModes.forEach((value) => params.append('run_mode', value));
      normalizedRunOrigins.forEach((value) => params.append('run_origin', value));
      provenanceQueryStates.forEach((value) => params.append('provenance_state', value));
      params.append('page', (pageParam as number).toString());

      const response = await apiClient.get<Runs>(get_automation_runs_url(automationId), {
        params,
      });

      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.next?.page ?? undefined,
    initialPageParam: 1,
    enabled: !!automationId && enabled,
  });
};

export default useRetrieveAutomationRuns;
